/**
 * @since 2025-02-19
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */
import { useEffect, useMemo } from "react";
import { AiOutlineLogout } from "react-icons/ai";
import "./MenuScheduler.scss";

import { getConfig } from "../../getConfig";

const config = getConfig();
const isAuthEnabled = (config.auth?.firebase?.enabled ?? config.ENABLE_FIREBASE_AUTHENTICATION) || (config.auth?.azure?.enabled ?? config.ENABLE_AZURE_AUTHENTICATION);

const MenuScheduler = ({
  schedulerSelected,
  listSchedulers,
  schedulersLoaded = false,
  dictJobs,
  dictSchedulerStatuses,
  dictMachineUsages,
  statusFilter = [],
  onStatusFilterClick,
  searchTerm = "",
  onSchedulerChange,
  onSearchChange,
  onLogout,
}) => {
  const listJobs = useMemo(() => {
    if (schedulerSelected === "ALL") {
      return Object.values(dictJobs).reduce(
        (acc, jobs) => acc.concat(jobs),
        []
      );
    } else {
      return dictJobs[schedulerSelected] || [];
    }
  }, [dictJobs, schedulerSelected]);

  const statusCounts = useMemo(() => {
    const counts = {
      WAIT: 0,
      RUN: 0,
      OK: 0,
      WARN: 0,
      ERR: 0,
      HOLD: 0,
      SKIP: 0,
    };

    listJobs.forEach((job) => {
      const s = (job.Status || "").trim().toUpperCase();
      if (s.includes("WAIT")) counts.WAIT++;
      else if (s.includes("RUN") || s.includes("TERM") || s.includes("START"))
        counts.RUN++;
      else if (s === "OK") counts.OK++;
      else if (s.includes("WARN")) counts.WARN++;
      else if (s.includes("ERR")) counts.ERR++;
      else if (s.includes("SKIP")) counts.SKIP++;
      else if (s.includes("HOLD")) counts.HOLD++;
    });

    return counts;
  }, [listJobs]);

  const filterStyles = {
    WAIT: "#666666",
    RUN: "#1976d2",
    OK: "#388e3c",
    WARN: "#d6932c",
    ERR: "#d32f2f",
    SKIP: "#6e6398",
    HOLD: "#b8b962",
  };

  const dictStatusMapping = {
    Running: "🟢",
    Loading: "⚪",
    "Not Running": "🔴",
    Partial: "🟠",
  };

  const filters = [
    { type: "WAIT", label: `WAIT(${statusCounts.WAIT})` },
    { type: "RUN", label: `RUN(${statusCounts.RUN})` },
    { type: "OK", label: `OK(${statusCounts.OK})` },
    { type: "WARN", label: `WARN(${statusCounts.WARN})` },
    { type: "ERR", label: `ERR(${statusCounts.ERR})` },
    { type: "HOLD", label: `HOLD(${statusCounts.HOLD})` },
    { type: "SKIP", label: `SKIP(${statusCounts.SKIP})` },
  ];

  useEffect(() => {
    // Wait for the first payload before falling back: until then the list is
    // just the "ALL" placeholder, and a scheduler restored from the URL would
    // be discarded before it ever had a chance to show up.
    if (!schedulersLoaded) return;
    if (
      listSchedulers.length > 0 &&
      !listSchedulers.includes(schedulerSelected)
    ) {
      onSchedulerChange(listSchedulers[0]);
    }
  }, [schedulersLoaded, listSchedulers, schedulerSelected, onSchedulerChange]);

  const handleFilterClick = (type) => {
    if (statusFilter.includes(type)) {
      onStatusFilterClick(statusFilter.filter((f) => f !== type));
    } else {
      onStatusFilterClick([...statusFilter, type]);
    }
  };

  return (
    <div className="menu-scheduler-container">
      <div className="menu-scheduler-toolbar">
        <div className="left-controls">
          <div className="scheduler-info">
            <select
              className="scheduler-select"
              value={schedulerSelected}
              onChange={(e) => onSchedulerChange(e.target.value)}
            >
              {listSchedulers.map((sch) => (
                <option key={sch} value={sch}>
                  {dictStatusMapping[dictSchedulerStatuses[sch]]} {sch}
                </option>
              ))}
            </select>
          </div>

          <div className="status-filters">
            {filters.map((f) => {
              const isSelected = statusFilter.includes(f.type);
              return (
                <span
                  key={f.type}
                  className={`filter-item ${isSelected ? "selected" : ""}`}
                  style={{ color: filterStyles[f.type] }}
                  onClick={() => handleFilterClick(f.type)}
                >
                  {f.label}
                </span>
              );
            })}
          </div>
        </div>

        <div className="right-controls">
          <input
            className="search-input"
            placeholder="Filter by name, hostname, or status"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
          {dictMachineUsages[schedulerSelected] && (
            <div className="status-checks">
              <span
                style={{
                  color:
                    dictMachineUsages[schedulerSelected]["cpu"] >= 90
                      ? "darkorange"
                      : undefined,
                }}
              >
                CPU: {dictMachineUsages[schedulerSelected]["cpu"]}%
              </span>
              <span
                style={{
                  color:
                    dictMachineUsages[schedulerSelected]["memory"] >= 90
                      ? "darkorange"
                      : undefined,
                }}
              >
                RAM: {dictMachineUsages[schedulerSelected]["memory"]}%
              </span>
              <span
                style={{
                  color:
                    dictMachineUsages[schedulerSelected]["disk"] <= 10
                      ? "darkorange"
                      : undefined,
                }}
              >
                Disk: {dictMachineUsages[schedulerSelected]["disk"]}GB
              </span>
            </div>
          )}
          {isAuthEnabled && (
            <button className="logout-button" onClick={() => onLogout(null)}>
              <AiOutlineLogout size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MenuScheduler;
